import React, { useState } from "react";
import Icon from "./icons";
import css from "./Scheduler.module.css";
import { MONTHS, MON_SHORT, startOfWeek, addDays, sameDay, startOfDay } from "./scheduleUtils";

const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1);
const endOfMonth = (d) => new Date(d.getFullYear(), d.getMonth() + 1, 0);
const fmt = (d) => `${MON_SHORT[d.getMonth()]} ${d.getDate()}`;

// Arbitrary date-range picker that reuses the Scheduler's WeekPicker design
// (presets rail + month calendar + footer). Two clicks set start then end; the
// selected span highlights as a black pill, rounded at the range ends and at
// each week's edges. Apply returns [start, end].
export default function DateRangePicker({ initialStart, initialEnd, maxDate, applyLabel = "Apply", onApply, onCancel }) {
  const today = startOfDay(new Date());
  const [start, setStart] = useState(initialStart ? startOfDay(initialStart) : null);
  const [end, setEnd] = useState(initialEnd ? startOfDay(initialEnd) : null);
  const [monthDate, setMonthDate] = useState(startOfMonth(initialStart || today));

  const maxD = maxDate ? startOfDay(maxDate) : null;
  const base = new Date(monthDate.getFullYear(), monthDate.getMonth(), 1);
  const gridStart = startOfWeek(base);
  const daysInMonth = new Date(base.getFullYear(), base.getMonth() + 1, 0).getDate();
  const totalCells = Math.ceil((base.getDay() + daysInMonth) / 7) * 7;
  const cells = Array.from({ length: totalCells }, (_, i) => addDays(gridStart, i));

  const pick = (d) => {
    const day = startOfDay(d);
    if (!start || (start && end)) {
      setStart(day);
      setEnd(null);
    } else if (day < start) {
      setEnd(start);
      setStart(day);
    } else {
      setEnd(day);
    }
  };

  const setRange = (s, e) => {
    setStart(startOfDay(s));
    setEnd(startOfDay(e));
    setMonthDate(startOfMonth(s));
  };

  const thisWkStart = startOfWeek(today);
  const presets = [
    { k: "thisweek", label: "This week", range: [thisWkStart, addDays(thisWkStart, 6)] },
    { k: "lastweek", label: "Last week", range: [addDays(thisWkStart, -7), addDays(thisWkStart, -1)] },
    { k: "thismonth", label: "This month", range: [startOfMonth(today), endOfMonth(today)] },
    { k: "lastmonth", label: "Last month", range: [new Date(today.getFullYear(), today.getMonth() - 1, 1), endOfMonth(new Date(today.getFullYear(), today.getMonth() - 1, 1))] },
  ];
  const matches = (r) => start && end && sameDay(start, r[0]) && sameDay(end, r[1]);
  const activePreset = presets.find((p) => matches(p.range))?.k || "custom";

  const rangeTxt = start && end
    ? `${fmt(start)} - ${fmt(end)}, ${end.getFullYear()}`
    : start
      ? `${fmt(start)} · select end date`
      : "Select a date range";

  return (
    <div className={css.dpPop}>
      <div className={css.dpRail}>
        {presets.map((p) => (
          <button
            key={p.k}
            type="button"
            className={`${css.dpPreset} ${activePreset === p.k ? css.active : ""}`}
            onClick={() => setRange(p.range[0], p.range[1])}
          >
            {p.label}
          </button>
        ))}
        <button type="button" className={`${css.dpPreset} ${activePreset === "custom" ? css.active : ""}`}>Custom</button>
      </div>
      <div className={css.dpMain}>
        <div className={css.dpTitle}>Select date range</div>
        <div className={css.dpHead}>
          <button type="button" className={css.dpNav} onClick={() => setMonthDate(new Date(base.getFullYear(), base.getMonth() - 1, 1))}>
            <Icon name="chevronLeft" size={16} />
          </button>
          <span className={css.dpMon}>{MONTHS[base.getMonth()]}</span>
          <span className={css.dpYr}>{base.getFullYear()}</span>
          <button type="button" className={css.dpNav} onClick={() => setMonthDate(new Date(base.getFullYear(), base.getMonth() + 1, 1))}>
            <Icon name="chevron" size={16} />
          </button>
        </div>
        <div className={css.dpGrid}>
          {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
            <div key={d} className={css.dpDow}>{d}</div>
          ))}
          {cells.map((d) => {
            const day = startOfDay(d);
            const out = d.getMonth() !== base.getMonth();
            const isToday = sameDay(d, today);
            const disabled = maxD ? day > maxD : false;
            const onlyStart = start && !end;
            const inSel = (start && end && day >= start && day <= end) || (onlyStart && sameDay(day, start));
            const isStart = start && sameDay(day, start);
            const isEnd = end ? sameDay(day, end) : isStart;
            const wkStart = isStart || (inSel && d.getDay() === 0);
            const wkEnd = isEnd || (inSel && d.getDay() === 6);
            return (
              <button
                key={d.toISOString()}
                type="button"
                disabled={disabled}
                className={[css.dpCell, out ? css.out : "", isToday ? css.today : "", inSel ? css.inWeek : "", wkStart ? css.wkStart : "", wkEnd ? css.wkEnd : ""].join(" ")}
                onClick={() => !disabled && pick(d)}
              >
                <span className={css.dpN}>{d.getDate()}</span>
              </button>
            );
          })}
        </div>
        <div className={css.dpFoot}>
          <span className={css.dpRange}>{rangeTxt}</span>
          <button type="button" className={css.dpCancel} onClick={() => onCancel?.()}>Cancel</button>
          <button type="button" className={css.dpApply} disabled={!start || !end} onClick={() => start && end && onApply?.([start, end])}>
            {applyLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
